import axios from "axios";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { LOG } from "../utils/logUtil";
import { useAppLoading } from "./useAppLoading";
import { useUsers } from "./useUsers";
import { useAlert } from "./useAlert";

interface IUser {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  OTP_RESET_TIME: number;
  socialId: string | null;
  pending_delete: boolean;
  deletion_date: string | null;
  qrCode: string | null;
  user_role?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  location: string;
  balance: number;
  totalRevenue: number;
  profileImage?: string;
  defaultLocation: string;
  kMeans?: number | null;
  invitedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface IStore {
  /** State */
  user: IUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  /** Callbacks */
  setLoginData: (
    user: IUser,
    accessToken: string,
    refreshToken: string
  ) => void;
  login: () => void;
  getUser: () => void;
  setUser: (user: IUser) => void;
  logout: () => void;
  // `reason` is recorded on the account closure record so an admin deletion is
  // attributable afterwards. Not sent for self-service deletions.
  permanentlyDeleteUser: (userId: string, reason?: string) => void;
  refreshAccessToken: () => void;
}

export const useAuth = create<IStore>()(
  persist(
    (set, get) => ({
      /** State */
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      /** Callbacks */
      setLoginData: (user, accessToken, refreshToken) => {
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        set({ user, accessToken, refreshToken, isAuthenticated: true });
      },
      login: () => {
        set({ isAuthenticated: true });
      },
      getUser: async () => {
        try {
          const response = await axios.get("/users/me");
          if (response?.status === 200) {
            set({ user: response?.data });
          }
        } catch (error) {
          LOG.error("Error on getUser", { error });
        }
      },
      setUser: (user) => {
        const currentUser = get().user;
        set({ user: { ...currentUser, ...user } });
      },
      logout: () => {
        axios.defaults.headers.common.Authorization = "";
        localStorage.clear();
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
        });
      },
      permanentlyDeleteUser: async (userId, reason) => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.delete(
            `/users/permanently-remove/${userId}`,
            { data: { reason: reason ?? null } }
          );
          if (response?.status === 200) {
            useAlert.getState().setAlert({
              isOpen: true,
              message: "You have successfully deleted the user.",
              severity: "success",
            });
          }
          if (response?.data?.message) {
            useAlert.getState().setAlert({
              isOpen: true,
              message: response?.data?.message,
              severity: "error",
            });
          }
        } catch (error) {
          LOG.error("Error on deleteUser", { error });
        } finally {
          useUsers.getState().getAllUsers();
          useAppLoading.getState().setIsLoading(false);
        }
      },
      refreshAccessToken: async () => {
        const currentRefreshToken = get().refreshToken;
        try {
          const body = {
            refreshToken: currentRefreshToken,
          };
          axios.defaults.headers.common.Authorization = "";
          const refreshTokenResponse = await axios.post(
            "/login/refreshToken",
            body
          );
          if (refreshTokenResponse?.data?.accessToken) {
            axios.defaults.headers.common.Authorization = `Bearer ${refreshTokenResponse?.data?.accessToken}`;
            set({
              user: refreshTokenResponse?.data?.user,
              accessToken: refreshTokenResponse?.data?.accessToken,
              refreshToken: refreshTokenResponse?.data?.refreshToken,
              isAuthenticated: true,
            });
          } else {
            alert("Session expired!");
            get().logout();
          }
        } catch (error) {
          LOG.error("Error on refreshAccessToken", { error });
          alert("Session expired!");
          get().logout();
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
